import React, { useCallback, useState, useEffect } from 'react';
import { Grid, Button, Typography, Tabs, Breadcrumbs, Tab, Tooltip, IconButton, Alert, Paper, Box } from '@mui/material';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import axios from 'axios';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/RefreshOutlined'
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { Colors } from '../landingPage/style/theme';
import CodeBlock from '../utils/CodeBlock';
import LinearProgress from '../components/linearProgress';
import ConfirmRegenerateAPIKeyDialog from '../components/dailog/ConfirmRegenerateAPIKeyDialog';


const SectionContainer = styled(Paper)`
  padding: 20px;
  margin-bottom: 20px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
`;

const SubHeader = styled(Typography)`
  font-size: 1.5rem;
  margin-top: 10px;
  margin-bottom: 10px;
`;

const CodeBlockStyle = styled.pre`
  background: #eaeaea;
  padding: 10px;
  border-radius: 4px;
  overflow-x: auto;
  font-family: monospace;
`;

const Section = ({ title, children }) => (
  <SectionContainer>
    <SubHeader>{title}</SubHeader>
    {children}
  </SectionContainer>
);

export default function IpToTimeAndLanguageCheckPage() {
  const [selectedTab, setSelectedTab] = useState(0);
  const userAPIToken = localStorage.getItem('apikey');
  const [copySuccess, setCopySuccess] = useState(false);
  const [isRunningPlan, setIsRunningPlan] = useState(false);
  // const [apiKeyHistory, setApiKeyHistory] = useState([]);

  const [isDialogOpen, setIsDialogOpen] = useState(false);


  const [subscriptionDetails, setSubscriptionDetails] = useState({
    subscriptionTitle: '',
    subscriptionDescription: '',
    subscriptionAmount: 0,
    subscriptionDays: 0,
    numberOfHit: 0,
    subsciptionPlanId: '',
    expiredOn: '',
    expiryEmailSent: false,
    createdAt: '',
    updatedAt: '',
    usedRequests: '',
    remainingRequests: '',
  });

  const requestFormat = `curl --location 'https://dev-api.innowave.solutions/ip_to_timezoneAndLocation' \\\n--header 'Content-Type: application/json' \\\n--header 'x-api-key: your_api_key_here' \\\n--data '{ "ip_address": "121.2.3.1" }'`;

  const openDialog = () => {
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
  };

  const confirmRegenerateToken = async () => {
    closeDialog();
    try {
      const userId = localStorage.getItem('userId');
      const token = localStorage.getItem('token');

      if (!token) {
        toast.warning('Token not found. Please login again.');
        return;
      }

      const cleanToken = token.replace(/"/g, '');

      const config = {
        headers: {
          Authorization: `Bearer ${cleanToken}`,
          'Content-Type': 'application/json',
        },
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/ReCreateApiKey`,
        { userId },
        config
      );

      if (response.data) {
        localStorage.setItem('apikey', response.data.data.newApiKeyValue);
        fetchPlans();
        toast.success('API key regenerated successfully!');
      } else {
        toast.error('Failed to regenerate API key.');
      }
    } catch (error) {
      toast.error('An error occurred while regenerating the API key.');
    }
  };


  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleCopyToken = () => {
    navigator.clipboard.writeText(userAPIToken);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const fetchPlans = useCallback(async () => {
    try {
      const userId = localStorage.getItem('userId');
      const token = localStorage.getItem('token');
      const cleanToken = token.replace(/"/g, '');
      const config = {
        headers: {
          Authorization: `Bearer ${cleanToken}`,
        },
      };
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/getPurchaseSubscriptionByuserId?userId=${userId}`,
        config
      );
      if (response.data && response.data.data.length > 0) {
        const matchedSubscription = response.data.data.find(sub => sub.subsciptionPlanId?.apiTitle === 'IP_TO_TIMEZONEANDLOCATION');

        if (matchedSubscription) {
          setIsRunningPlan(true);
          const purchaseSubscriptionId = matchedSubscription._id;

          try {
            const requestResponse = await axios.get(
              `${process.env.REACT_APP_API_URL}/getRemainingRequests?userId=${userId}&purchaseSubscriptionId=${purchaseSubscriptionId}`,
              config
            );

            if (requestResponse.data) {
              setSubscriptionDetails({
                subscriptionTitle: matchedSubscription.subsciptionPlanId.subscriptionTitle || 'Basic',
                subscriptionDescription: matchedSubscription.subsciptionPlanId.subscriptionDescription || '',
                subscriptionAmount: matchedSubscription.subsciptionPlanId.subscriptionAmount || 0,
                subscriptionDays: matchedSubscription.subsciptionPlanId.subscriptionDays || 0,
                numberOfHit: matchedSubscription.subsciptionPlanId.numberOfHit || 0,
                expiredOn: matchedSubscription.expiredOn || '',
                expiryEmailSent: matchedSubscription.expiryEmailSent || false,
                createdAt: matchedSubscription.createdAt || '',
                updatedAt: matchedSubscription.updatedAt || '',
                usedRequests: requestResponse.data.data.usedRequests || 0,
                remainingRequests: requestResponse.data.data.remainingRequests || 0,
              });
            }
          } catch (error) {
            console.error("Error fetching remaining requests:", error);
          }
        } else {
          setIsRunningPlan(false);
        }
      } else {
        setIsRunningPlan(false);
      }
    } catch (error) {
      setIsRunningPlan(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  return (
    <Grid container spacing={3} sx={{ p: 4 }}>
      {/* API Key Section */}
      <Grid item xs={12}>
        <Grid item xs={12} mb={4}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link to="/dashboard" style={{ textDecoration: 'none', color: Colors.primary }}>
              Dashboard
            </Link>
            <Typography>IP to Time-Zone and Location Data API</Typography>
          </Breadcrumbs>
        </Grid>

        <Typography variant="body1" sx={{ fontWeight: 'bold', color: Colors.primary, mb: 1 }}>
          Your API Key:
        </Typography>
        <Grid container alignItems="center" sx={{ background: '#eaeaea', p: 1, borderRadius: '4px' }}>
          <Grid item xs>
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'monospace',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {isRunningPlan ? userAPIToken : 'Purchase any subscription plan to generate an API key.'}
            </Typography>
          </Grid>
          <Grid item>
            <Tooltip title={copySuccess ? 'Copied!' : 'Copy'}>
              <IconButton onClick={handleCopyToken} disabled={!isRunningPlan}>
                <ContentCopyIcon sx={{ fontSize: 20, color: Colors.primary }} />
              </IconButton>
            </Tooltip>
          </Grid>
          <Grid item>
            <Tooltip title="Regenerate API Key">
              <IconButton onClick={openDialog} disabled={!isRunningPlan}>
                <RefreshIcon sx={{ fontSize: 20, color: Colors.primary }} />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>

        {/* {apiKeyHistory.length > 0 && (
          <Grid item xs={12} mt={2}>
            <Typography variant="body1" sx={{ fontWeight: 'bold', color: Colors.primary }}>
              API Key History:
            </Typography>
            <List>
              {apiKeyHistory.map((key, index) => (
                <ListItem key={index} disablePadding>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {key}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Grid>
        )} */}
      </Grid>


      {/* Tabs */}
      <Grid item xs={12}>
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          aria-label="area tabs"
          indicatorColor="secondary"
          textColor="secondary"
        >
          <Tab label="QuickStart Guidance" />
          <Tab label="Documentation" />
        </Tabs>
      </Grid>

      {/* Content Based on Tab */}
      {selectedTab === 0 &&
        (isRunningPlan ? (
          <Grid item xs={12}>
            <Grid item xs={12} my={3}>
              <LinearProgress data={subscriptionDetails} />
            </Grid>
            <Alert severity="info">The value of API usage does not update instantly based on user activity; it may take a minimum of 20 minutes for the changes to be reflected.</Alert>
            <Grid item xs={12} my={3}>
              <Box
                sx={{
                  p: 3,
                  borderRadius: '8px',
                  backgroundColor: '#f9f9f9',
                  border: `1px solid ${Colors.primary}`,
                }}
              >
                {/* Subscription Title */}
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: Colors.primary, mb: 1 }}>
                  Subscription Details
                </Typography>

                {/* Subscription Plan */}
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Plan: <strong>{subscriptionDetails.subscriptionTitle}</strong>
                </Typography>

                {/* Subscription Amount */}
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Amount: <strong>{subscriptionDetails.subscriptionAmount}</strong>
                </Typography>

                {/* Subscription Days */}
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Duration: <strong>{subscriptionDetails.subscriptionDays} days</strong>
                </Typography>

                {/* Subscription Description */}
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Description: <strong>{subscriptionDetails.subscriptionDescription}</strong>
                </Typography>

                {/* Usage Period */}
                <Box
                  sx={{
                    mt: 2,
                    pt: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: Colors.secondary }}>
                    Validity Period
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {new Date(subscriptionDetails.createdAt).toLocaleDateString()} -{' '}
                    {new Date(subscriptionDetails.expiredOn).toLocaleDateString()}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        ) : (
          <Grid item xs={12}>
            <Typography
              variant="body1"
              sx={{
                textAlign: 'center',
                color: Colors.secondary,
                backgroundColor: Colors.light_gray,
                padding: 2,
                borderRadius: '8px',
              }}
            >
              You currently don't have a subscription plan for the IP to Time-Zone and Location Data API. Please subscribe to a plan to take
              full advantage of the API's features.
              <Button
                component={Link}
                to="/dashboard/subscription"
                state={{ openAccordionId: 'panel1' }}
                sx={{
                  color: Colors.primary,
                  textDecoration: 'underline',
                  fontWeight: 'bold',
                }}
              >
                Buy Plan
              </Button>
            </Typography>
          </Grid>
        ))}

      {selectedTab === 1 && (
        <Grid item xs={12}>
          <Section title="Overview">
            <Typography>
              This API provides detailed geolocation, connection type, and time-zone information for any given IP
              address.
            </Typography>
          </Section>

          <Section title="Base URL">
            <Typography>
              <strong>Dev:</strong> https://dev-api.innowave.solutions/ip_to_timezoneAndLocation
              <br />
              <strong>Prod:</strong> https://api.innowave.solutions/ip_to_timezoneAndLocation
            </Typography>
          </Section>

          <Section title="Endpoint">
            <Typography variant="h6">Check IP Quality and Location</Typography>
            <Typography variant="h6">Request Format</Typography>
            <CodeBlockStyle>
              <CodeBlock>{requestFormat}</CodeBlock>
            </CodeBlockStyle>
          </Section>

          <Grid container justifyContent="flex-start" sx={{ mt: 4 }}>
            <Typography>
              For a complete and detailed guide, visit our{' '}
              <Button
                component={Link}
                to="/dashboard/documentation"
                state={{ openAccordionId: 'panel1' }}
                sx={{
                  color: Colors.primary,
                  textDecoration: 'underline',
                  fontWeight: 'bold',
                }}
              >
                API Documentation
              </Button>
            </Typography>
          </Grid>
        </Grid>
      )}
      <ConfirmRegenerateAPIKeyDialog
        open={isDialogOpen}
        onClose={closeDialog}
        onConfirm={confirmRegenerateToken}
      />
    </Grid>
  );
}

Section.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};
